import { useLocalStorageContext } from '@src/contexts';
import {
  ContactIcon,
  ExtensionAttributeIcon,
  IdHelper,
  Permissions,
  ProjectCustomizations,
  ProjectModel,
  ProjectPermissions,
  ProjectRepository,
  ProjectSetting,
  ProjectStepCollectModel,
  ProjectType,
} from '@src/core';
import { Features } from '@src/core/features';
import { DateHelper } from '@src/core/helpers/date.helper';
import { ProjectSettings } from '@src/project-settings';
import { sortBy } from 'lodash';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useSessionContext } from '../authentication';
import { LocalStorageCacheKey } from './constants';

const ProjectContext = createContext<any>(null);

const ProjectProvider: FC<PropsWithChildren> = ({ children }) => {
  const { projectsAllowed, signInCount, isAdmin, userLogged } = useSessionContext();
  const { getLocalStorage, removeFromLocalStorage, saveLocalStorage } = useLocalStorageContext();

  // cognito mode
  const [cognitoMode, setCognitoMode] = useState<boolean>(false);
  // end: cognito mode

  // reference date
  const [referenceDateOverride, setReferenceDateOverride] = useState<Date | null>(() => {
    const storedDate = getLocalStorage(LocalStorageCacheKey.REFERENCE_DATE);
    if (storedDate === null) return null;
    return DateHelper.getDateBRTimezone(new Date(storedDate));
  });

  // memoized because the consumers watch referenceDate in effect dependencies
  const referenceDate = useMemo(() => referenceDateOverride ?? DateHelper.getDateBRTimezone(), [referenceDateOverride]);

  useEffect(() => {
    if (referenceDateOverride === null) {
      removeFromLocalStorage(LocalStorageCacheKey.REFERENCE_DATE);
      return;
    }

    saveLocalStorage(LocalStorageCacheKey.REFERENCE_DATE, referenceDateOverride.toISOString());
  }, [referenceDateOverride]);

  /*
    Entrar de novo recomeça no presente: a data escolhida numa sessão anterior
    não deve decidir o que é "hoje" para quem acabou de entrar.

    Reage ao contador de sign-in, e não a userLogged, porque userLogged também
    vira true em cada revalidação -- a data então não sobreviveria a um F5, e a
    chave do localStorage seria lida no boot só para ser apagada em seguida,
    piscando o app para a data antiga antes de voltar.

    Declarado acima do efeito que carrega os projetos de propósito: efeitos
    rodam na ordem em que aparecem, então referenceDate já é hoje quando o
    realtimeDbPath aparece, e os watchers do Realtime DB assinam uma vez em vez
    de assinar e reassinar.
  */
  useEffect(() => {
    if (signInCount === 0) return;

    setReferenceDateOverride(null);
  }, [signInCount]);

  /*
    Só o perfil admin escolhe a data de referência, então ninguém mais pode
    acabar preso numa: a data some para quem não é admin, inclusive quando foi
    deixada no navegador por um admin que usou a mesma máquina.

    O guard de userLogged é obrigatório. isAdmin nasce falso e só recebe o valor
    real quando a sessão resolve o perfil; sem ele o efeito rodaria durante o
    boot e apagaria a data de um admin a cada recarregamento. Quando userLogged
    vira true o perfil já está decidido -- as duas coisas acontecem na mesma
    passagem de validateUser, com o perfil antes.
  */
  useEffect(() => {
    if (userLogged === false || isAdmin === true) return;

    setReferenceDateOverride(null);
  }, [userLogged, isAdmin]);

  // project data
  const [projects, setProjects] = useState<Map<string, ProjectModel>>(new Map<string, ProjectModel>());

  const [projectCollectionName, setProjectCollectionName] = useState<string>('');

  const [projectId, setProjectId] = useState<string>(getLocalStorage(LocalStorageCacheKey.LAST_USED_PROJECT));
  const [projectLogo, setProjectLogo] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [projectIsProduction, setProjectIsProduction] = useState<boolean>(false);
  const [projectIsArchived, setProjectIsArchived] = useState<boolean>(false);
  const [projectType, setProjectType] = useState<ProjectType>(ProjectType.forms);
  const [projectPermissions, setProjectPermissions] = useState<ProjectPermissions>({});
  const [projectFeatures, setProjectFeatures] = useState<Features[]>([]);

  const [projectSetting, setProjectSetting] = useState<ProjectSetting>({} as ProjectSetting);

  useEffect(() => {
    saveLocalStorage(LocalStorageCacheKey.LAST_USED_PROJECT, projectId);
  }, [projectId]);

  const setActiveProjectData = (project: ProjectModel) => {
    setProjectId(project.id);
    setProjectCollectionName(project.collectionName);
    setProjectLogo(project.logo);
    setProjectName(project.name);
    setProjectIsProduction(project.isProduction);
    setProjectIsArchived(project.isArchived === true);
    setProjectType(project.type);
    setProjectPermissions(project.permissions);
    setProjectFeatures(project.features);

    setCollects(new Map<string, ProjectStepCollectModel>(Object.entries(project.collects)));
    setFirstCollect(project.firstCollect);

    setCustomizations(project.customizations);

    setProjectSetting(ProjectSettings[project.id] ?? ({} as ProjectSetting));
  };
  // end: project data

  // collects
  const [collects, setCollects] = useState<Map<string, ProjectStepCollectModel>>(
    new Map<string, ProjectStepCollectModel>()
  );
  const [firstCollect, setFirstCollect] = useState<1 | 2>(1);
  // end: collects

  // firebase base path
  const [firestorePath, setFirestorePath] = useState<[string] | false>(false);
  const [realtimeDbPath, setRealtimeDbPath] = useState<[string] | false>(false);

  useEffect(() => {
    if (!projectCollectionName || !projectId) return;
    setFirestorePath([projectCollectionName]);
    setRealtimeDbPath([projectId]);
    document.title = `${projectName} by e-método`;
  }, [projectCollectionName, projectId, projectName]);
  // end: firebase base path

  const [customizations, setCustomizations] = useState<ProjectCustomizations>({
    contactToday: {
      firstContact: {
        label: 'Primeiro contato',
        noResult: 'Não há contatos para hoje',
      },
      reminder: {
        label: 'Retentativas de contato',
        noResult: 'Não há contatos para hoje',
      },
    },
    deliveredResponseToday: {
      title: 'Respostas Recebidas',
      description:
        'Veja aqui a lista dos contatos que responderam ao formulário nas últimas 24 horas, e envie uma mensagem de agradecimento!',
      nothingPendingLabel: 'Não há agradecimentos para enviar',
    },
    contact: {
      extensionAttributes: [],
      useEmail: true,
      useSequence: true,
      useWhatsApp: true,
    },
    contactSearch: {
      collectLabels: {
        successfullyAt: 'Contatado em',
      },
      referenceDateLabel: 'Data de Referência',
      extensionAttributesLabel: 'Informações',
      contactIcon: ContactIcon.woman,
      extensionAttributesIcon: ExtensionAttributeIcon.hospital,
    },
  });

  useEffect(() => {
    projects.clear();

    const fetchData = async (projectId: string) => await ProjectRepository.getById(projectId);

    const loadProjects = async () => {
      const currentProjects = new Map<string, ProjectModel>();

      await Promise.all(
        projectsAllowed.map(async projectId => {
          const data = await fetchData(projectId);
          if (data === null) return;
          currentProjects.set(projectId, data);
        })
      );

      setProjects(currentProjects);

      const projectData = currentProjects.get(projectId);

      if (projectData !== undefined) {
        setActiveProjectData(projectData);
        return;
      }

      const firstProject = currentProjects.values().next().value;

      if (firstProject !== undefined) {
        setActiveProjectData(firstProject);
      }
    };

    loadProjects();
  }, [projectsAllowed]);

  const activeEnvironmentProject = (projectId: string) => {
    const project = projects.get(projectId);
    if (project === undefined) return;

    setActiveProjectData(project);
  };

  const allowedFeature = (feature: Features) => projectFeatures.includes(feature);

  const checkPolicy = (collectNumber: number, permission: Permissions) => {
    const { permissions = {} } = collects.get(`collect${IdHelper.formatCollectId(collectNumber)}`) ?? {};
    return permissions[permission] ?? projectPermissions[permission] ?? false;
  };

  return (
    <ProjectContext.Provider
      value={{
        // project data
        projects: sortBy(Array.from(projects.values()), 'name'),
        projectId,
        projectLogo,
        projectName,
        projectIsProduction,
        projectIsArchived,
        projectType,
        projectPermissions,
        projectSetting,
        isCalendarMode: projectType === ProjectType.calendar,
        isFormsMode: projectType === ProjectType.forms,
        // collects
        collects: sortBy(Array.from(collects.values()), 'id'),
        firstCollect,
        // firebase base path
        firestorePath,
        realtimeDbPath,
        // others
        activeEnvironmentProject,
        allowedFeature,
        customizations,
        checkPolicy,
        // referenceDate
        referenceDate,
        referenceDateOverride,
        setReferenceDateOverride,
        // cognito mode
        cognitoMode,
        setCognitoMode,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

interface ProjectContextProps {
  // project data
  projects: ProjectModel[];
  projectId: string;
  projectLogo: string | null;
  projectName: string;
  projectIsProduction: boolean;
  projectIsArchived: boolean;
  projectType: ProjectType;
  projectPermissions: ProjectPermissions;
  projectSetting: ProjectSetting;
  isCalendarMode: boolean;
  isFormsMode: boolean;
  // collects
  collects: ProjectStepCollectModel[];
  firstCollect: 1 | 2;
  // firebase base path
  firestorePath: [string, string] | false;
  realtimeDbPath: [string] | false;
  // others
  activeEnvironmentProject: (projectId: string) => void;
  allowedFeature: (feature: Features) => boolean;
  customizations: ProjectCustomizations;
  checkPolicy: (collectNumber: number, permission: Permissions) => boolean;
  // referenceDate
  referenceDate: Date;
  referenceDateOverride: Date | null;
  setReferenceDateOverride: (d: Date | null) => void;
  // cognito mode
  cognitoMode: boolean;
  setCognitoMode: (b: boolean) => void;
}

const useProjectContext = (): ProjectContextProps => useContext(ProjectContext);

export { ProjectProvider, useProjectContext };
